import { Box, LinearProgress, Stack, Typography } from '@mui/material';
import React from 'react';
import i18n from '../../i18n';
import DialogComponent from '../dialog/dialog.component';
import './archive-ocr-parsing-dialog.scss';

/** 每一步点亮间隔（ms），对齐原型 openParse。 */
const STEP_INTERVAL_MS = 230;

/** 识别步骤 i18n key 列表（顺序即展示顺序）。 */
const OCR_STEP_KEYS = [
    'archive_form_ocr_step_upload',
    'archive_form_ocr_step_detect',
    'archive_form_ocr_step_extract',
    'archive_form_ocr_step_map',
] as const;

/** OCR 识别进度弹窗参数。 */
export type ArchiveOcrParsingDialogProps = {
    /** 是否显示弹窗。 */
    open: boolean;
    /** 当前识别中的文件名（作标题）。 */
    fileName: string;
};

/**
 * 登记文件 OCR 识别进度弹窗：文件名 + 不确定进度条 + 步骤逐条打勾。
 * @param props 开关与文件名
 * @returns 识别进度弹窗
 */
export function ArchiveOcrParsingDialog({
    open,
    fileName,
}: ArchiveOcrParsingDialogProps): JSX.Element {
    /** 已完成（打勾）的步骤数量，0～步骤总数。 */
    const [completedCount, setCompletedCount] = React.useState(0);

    React.useEffect(() => {
        if (!open) {
            setCompletedCount(0);
            return undefined;
        }
        setCompletedCount(0);
        /** 下一步点亮的定时器句柄列表，卸载时清理。 */
        const timers: number[] = [];
        OCR_STEP_KEYS.forEach((_, index) => {
            // 按间隔依次点亮第 index+1 步
            const timerId = window.setTimeout(() => {
                setCompletedCount(index + 1);
            }, STEP_INTERVAL_MS * (index + 1));
            timers.push(timerId);
        });
        return () => {
            timers.forEach((id) => window.clearTimeout(id));
        };
    }, [open]);

    return (
        <DialogComponent
            open={open}
            maxWidth='sm'
            disableEscapeKeyDown
            onClose={() => undefined}
            defaultHeaderProps={{
                title: '',
                isHiddenCloseIcon: true,
            }}
            footer={<></>}
            className='archive-ocr-parsing-dialog'
            sx={{
                '& .MuiDialog-container > .MuiPaper-root': {
                    maxWidth: '420px',
                    border: 0,
                    borderRadius: '12px',
                    background: 'var(--fill-neutral-100)',
                },
                '& .MuiDialogTitle-root': {
                    display: 'none',
                    padding: 0,
                },
                '& .dialog-footer': {
                    display: 'none',
                },
            }}
            dialogContentStyle={{ paddingTop: 0 }}
        >
            <Box className='archive-ocr-parsing-dialog__body'>
                <Typography
                    variant='subtitle1'
                    component='h2'
                    className='archive-ocr-parsing-dialog__title'
                    aria-label={i18n.t('archive_form_ocr_parsing')}
                >
                    {fileName}
                </Typography>
                <LinearProgress
                    variant='indeterminate'
                    className='archive-ocr-parsing-dialog__progress'
                    aria-label={i18n.t('archive_form_ocr_parsing')}
                />
                <Stack
                    component='ul'
                    className='archive-ocr-parsing-dialog__steps'
                    spacing={0}
                >
                    {OCR_STEP_KEYS.map((stepKey, index) => {
                        /** 当前步骤是否已完成（应显示打勾）。 */
                        const done = index < completedCount;
                        return (
                            <Typography
                                key={stepKey}
                                component='li'
                                variant='body2'
                                className={
                                    done
                                        ? 'archive-ocr-parsing-dialog__step archive-ocr-parsing-dialog__step--done'
                                        : 'archive-ocr-parsing-dialog__step'
                                }
                            >
                                {done ? (
                                    <>
                                        <Box
                                            component='span'
                                            className='archive-ocr-parsing-dialog__check'
                                            aria-hidden
                                        >
                                            ✓{' '}
                                        </Box>
                                        {i18n.t(stepKey)}
                                    </>
                                ) : (
                                    i18n.t(stepKey)
                                )}
                            </Typography>
                        );
                    })}
                </Stack>
            </Box>
        </DialogComponent>
    );
}
